import React from 'react'
import { Building2, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Branch } from '@/store/auth.store'

interface BranchSelectionProps {
  branches: Branch[]
  onBranchSelect: (branch: Branch) => void
  isLoading?: boolean
  /**
   * When true, renders the selector as an embedded, compact block
   * without the full-screen centering wrapper. Useful when composing
   * this inside other pages (e.g., Login) to avoid large vertical gaps.
   */
  compact?: boolean
}

/**
 * Branch selection component displayed after login
 * Allows user to select which branch to work with
 */
export const BranchSelection: React.FC<BranchSelectionProps> = ({
  branches,
  onBranchSelect,
  isLoading = false,
  compact = false,
}) => {

  const content = (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold text-primary">Available Branches</h3>
          <p className="text-sm text-muted-foreground">
            Select the branch you want to work with
          </p>
        </div>

        {/* Direct Branch Buttons */}
        <div className="space-y-3">
          {branches.map((branch) => (
            <Button
              key={branch.branchId}
              variant="outline"
              className="w-full h-auto p-4 text-left justify-start border-2 hover:border-primary hover:bg-primary/5 transition-all"
              onClick={() => onBranchSelect(branch)}
              disabled={isLoading}
            >
              <div className="flex items-center w-full">
                <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {branch.branchName}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Branch ID: {branch.branchId} • Role ID: {branch.branchUserRoleId}
                  </div>
                </div>
                {isLoading && (
                  <Loader2 className="w-4 h-4 ml-2 animate-spin flex-shrink-0" />
                )}
              </div>
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  )

  if (compact) {
    // Embedded mode: return only the card without full-screen wrapper
    return <div className="w-full max-w-md">{content}</div>
  }

  // Default full-screen centered layout
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">{content}</div>
    </div>
  )
} 